import clsx from 'clsx';
import { createContext, use, useCallback, useMemo } from 'react';
import { PiCheckBold } from 'react-icons/pi';

interface MultiSelectListContext<TValue = unknown> {
  selectedValues: TValue[];
  toggleItem: (value: TValue) => void;
}

const MultiSelectListContext = createContext<MultiSelectListContext>(undefined!);

export function MultiSelectList<TValue>({
  children,
  onChange,
  value
}: React.PropsWithChildren<{
  onChange: (value: TValue[]) => void;
  value?: TValue[] | null;
}>) {
  const toggleItem = useCallback(
    (item: TValue) => {
      if (!value?.includes(item)) {
        onChange([...(value || []), item]);
      } else {
        onChange(value.filter(v => v !== item));
      }
    },
    [onChange, value]
  );

  const context = useMemo(
    () => ({
      selectedValues: (value || []) as unknown[],
      toggleItem: toggleItem as (value: unknown) => void
    }),
    [toggleItem, value]
  );

  return (
    <MultiSelectListContext value={context}>
      <div className="flex flex-col gap-y-0.5">{children}</div>
    </MultiSelectListContext>
  );
}

export function MultiSelectListOption<TValue = unknown>({
  children,
  className,
  value
}: React.PropsWithChildren<{
  className?: string;
  value: TValue;
}>) {
  const { selectedValues, toggleItem } = use(MultiSelectListContext);

  const handleClick = useCallback(() => {
    toggleItem(value);
  }, [toggleItem, value]);

  const isSelected = selectedValues.includes(value);

  return (
    <button
      className={clsx(
        'group flex items-center justify-between rounded-md px-2.5 py-2 hover:bg-primary-50 focus-visible:outline-primary-500',
        isSelected && 'bg-primary-50',
        className
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-x-2">{children}</div>

      <div className="ml-4 size-4">
        {isSelected && <PiCheckBold className="size-full text-primary-600" />}
      </div>
    </button>
  );
}
